import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import UserFormModal from '../components/UserFormModal';
import UserDeleteModal from '../components/UserDeleteModal';
import Pagination from '../components/Pagination';
import './Users.css';

const Users = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [searching, setSearching] = useState('');
  const [filterRole, setFilterRole] = useState('todos');
  const [filterConcesionario, setFilterConcesionario] = useState('todos');
  const [concesionarios, setConcesionarios] = useState([]);

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Verificar permisos
  useEffect(() => {
    const isAllowed = user?.role === 'superadmin' || ['superadmin', 'admin_transito', 'admin_concesionario', 'admin_punto_venta'].includes(user?.profile_name) || ['superadmin', 'admin_transito', 'admin_concesionario', 'admin_punto_venta', 'admin'].includes(user?.role);
    if (!isAllowed) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const [usersRes, concsRes] = await Promise.all([
        axios.get('/api/auth/users', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get('/api/concesionarios')
      ]);
      setUsers(usersRes.data);
      setConcesionarios(concsRes.data);
      setError('');
    } catch (err) {
      setError('Error al cargar usuarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (userToEdit = null) => {
    setEditingUser(userToEdit);
    setShowFormModal(true);
  };

  const handleCloseForm = () => {
    setEditingUser(null);
    setShowFormModal(false);
  };

  const handleSaveUser = async (data) => {
    try {
      if (editingUser) {
        await axios.put(`/api/auth/users/${editingUser.id}`, data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
      } else {
        await axios.post('/api/auth/create-user', data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
      }
      fetchUsers();
      handleCloseForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar usuario');
    }
  };

  const handleOpenDelete = (userToDelete) => {
    setUserToDelete(userToDelete);
    setShowDeleteModal(true);
  };

  const handleCloseDelete = () => {
    setUserToDelete(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/api/auth/users/${userToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      fetchUsers();
      handleCloseDelete();
    } catch (err) {
      setError('Error al eliminar usuario');
    }
  };

  const getFilteredUsers = () => {
    let filtered = users;

    if (searching.trim()) {
      const term = searching.toLowerCase();
      filtered = filtered.filter(u =>
        u.username.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term)
      );
    }

    if (filterRole !== 'todos') {
      filtered = filtered.filter(u => u.role === filterRole || u.profile_name === filterRole);
    }

    if (filterConcesionario !== 'todos') {
      if (filterConcesionario === 'sin_concesionario') {
        filtered = filtered.filter(u => !u.concesionario_id && !u.concesionario_name);
      } else {
        filtered = filtered.filter(u => String(u.concesionario_id) === String(filterConcesionario) || u.concesionario_name === filterConcesionario);
      }
    }

    return filtered;
  };

  const filteredUsers = getFilteredUsers();

  // Exportar usuarios a Excel
  const exportToExcel = () => {
    const exportData = filteredUsers.map(u => ({
      'ID': u.id,
      'Usuario': u.username,
      'Nombre Completo': u.full_name || '',
      'Cédula': u.document_id || '',
      'Email': u.email,
      'Rol': u.role === 'superadmin' ? 'Superadmin' : u.role === 'admin' ? 'Admin' : 'Usuario',
      'Perfil': u.profile_name || 'Ninguno',
      'Asociación (Tránsito/Concesionario)': u.transito_name ? `Tránsito: ${u.transito_name}` : u.concesionario_name ? `Concesionario: ${u.concesionario_name}` : 'Global',
      'Fecha Creación': u.created_at ? new Date(u.created_at).toLocaleDateString('es-CO') : ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');
    XLSX.writeFile(workbook, `Usuarios_${new Date().toLocaleDateString('es-CO').replace(/\//g, '-')}.xlsx`);
  };

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searching, filterRole, filterConcesionario]);

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />

      <div className="main-content">
        <Navbar title="👥 Gestión de Usuarios" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          <div className="page-header">
            <div className="header-title">
              <h2>Usuarios del Sistema</h2>
              <p className="subtitle">Total: {users.length} usuarios registrados ({filteredUsers.length} mostrados)</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={exportToExcel} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                📊 Exportar a Excel
              </button>
              {user?.profile_name !== 'administrativo_concesionario' && (
              <button onClick={() => handleOpenForm()} className="btn-primary">
                ➕ Nuevo Usuario
              </button>
              )}
            </div>
          </div>

          <div className="filters-bar">
            <div className="filter-group">
              <label>🔍 Buscar:</label>
              <input
                type="text"
                placeholder="Usuario o correo..."
                value={searching}
                onChange={(e) => setSearching(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>🛡️ Perfil:</label>
              <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                <option value="todos">Todos los Perfiles</option>
                <option value="superadmin">Superadmin</option>
                <option value="admin_transito">Admin Tránsito</option>
                <option value="admin_concesionario">Admin Concesionario</option>
                <option value="auxiliar_concesionario">Auxiliar Concesionario (Lectura)</option>
                <option value="administrativo_concesionario">Administrativo Concesionario (Lectura, todos los puntos de venta)</option>
                <option value="admin_punto_venta">Admin Punto de Venta</option>
                <option value="asesor">Asesor</option>
              </select>
            </div>
            <div className="filter-group">
              <label>🏪 Concesionario:</label>
              <select value={filterConcesionario} onChange={(e) => setFilterConcesionario(e.target.value)}>
                <option value="todos">Todos los Concesionarios</option>
                <option value="sin_concesionario">Sin Concesionario (Global / Tránsito)</option>
                {concesionarios.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <p className="loading">Cargando usuarios...</p>
          ) : filteredUsers.length === 0 ? (
            <p className="no-data">No hay usuarios que coincidan con los filtros</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Nombre Completo</th>
                    <th>Cédula</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Perfil</th>
                    <th>Asociación</th>
                    <th>Punto de Venta</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(u => (
                    <tr key={u.id}>
                      <td className="user-name">{u.username}</td>
                      <td>{u.full_name || '-'}</td>
                      <td>{u.document_id || '-'}</td>
                      <td className="user-email">{u.email}</td>
                      <td>
                        <span className={`role-badge role-${u.role}`}>
                          {u.role === 'superadmin' ? '🔐 Superadmin' : u.role === 'admin' ? '👨‍💼 Admin' : '👤 Usuario'}
                        </span>
                      </td>
                      <td>
                        {u.profile_name ? (
                          <span className="profile-badge">🛡️ {u.profile_name}</span>
                        ) : (
                          <span className="no-profile">Ninguno</span>
                        )}
                      </td>
                      <td>
                        {u.transito_name ? (
                          <span className="transito-badge">🏢 {u.transito_name}</span>
                        ) : u.concesionario_name ? (
                          <span className="concesionario-badge">🏪 {u.concesionario_name}</span>
                        ) : (
                          <span className="no-transito">Global</span>
                        )}
                      </td>
                      <td>
                        {u.punto_venta_name ? (
                          <span style={{ fontWeight: '600', color: '#059669', backgroundColor: '#ecfdf5', padding: '4px 8px', borderRadius: '4px' }}>📍 {u.punto_venta_name}</span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>—</span>
                        )}
                      </td>
                      <td className="actions">
                        {user?.profile_name !== 'administrativo_concesionario' && (<>
                        <button
                          onClick={() => handleOpenForm(u)}
                          className="btn-action btn-edit"
                          title="Editar usuario"
                        >
                          ✏️ Editar
                        </button>
                        {u.id !== user?.id && (
                          <button
                            onClick={() => handleOpenDelete(u)}
                            className="btn-action btn-delete"
                            title="Eliminar usuario"
                          >
                            🗑️ Eliminar
                          </button>
                        )}
                        </>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              
              <Pagination 
                currentPage={currentPage}
                totalItems={filteredUsers.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>

        <UserFormModal
          isOpen={showFormModal}
          user={editingUser}
          onClose={handleCloseForm}
          onSubmit={handleSaveUser}
        />

        <UserDeleteModal
          isOpen={showDeleteModal}
          user={userToDelete}
          onClose={handleCloseDelete}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
};

export default Users;
